import React, { useState, useMemo } from "react";
import { useInvoices } from "../hooks/useInvoices.js";
import { getInvoiceById } from "../services/invoiceService.js";
import { InvoiceCreateFlow } from "../components/InvoiceCreateFlow.jsx";
import { InvoiceDetailModal } from "../components/InvoiceDetailModal.jsx";
import { PaymentTallyModal } from "../components/PaymentTallyModal.jsx";
import { PaymentHistoryModal } from "../components/PaymentHistoryModal.jsx";
import { PaymentReceiptModal } from "../components/PaymentReceiptModal.jsx";
import { GstReportModal } from "../../reports/components/GstReportModal.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { OtherCompanyPrompt } from "../../../shared/components/OtherCompanyPrompt.jsx";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../../../shared/utils/companyScope.js";
import { useIncrementalList } from "../../../shared/hooks/useIncrementalList.js";
import { ListSentinel } from "../../../shared/components/ListSentinel.jsx";
import { useRoutedSelection } from "../../../shared/hooks/useRoutedSelection.js";
import { useListView } from "../../../shared/hooks/useListView.js";
import { DataTable, sortRows, readStoredSort } from "../../../shared/components/DataTable.jsx";
import { SplitPane, RecordList, RecordNav, useSplitScroll } from "../../../shared/components/SplitView.jsx";
import { paymentStatusVariant } from "../../../shared/components/tableColumns.jsx";
import { useSetPageHeading } from "../../../shared/context/PageHeadingContext.jsx";
import { ViewToggle, StickyFooter } from "../../../shared/components/ListChrome.jsx";
import { invoiceColumns } from "../components/invoiceColumns.jsx";

export function InvoicesPage() {
  const { invoices, loading, error, addInvoice, recordPayment, removeInvoice, refetch: refetchInvoices } = useInvoices();
  const { companies, defaultCompany } = useCompanies();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL"); // 'ALL' | 'UNPAID' | 'PAID'
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  // Edit starts from a fresh copy, so a payment recorded a moment ago is included
  const openEditor = async (inv) => {
    try {
      setEditingInvoice((await getInvoiceById(inv.id)) || inv);
    } catch (_) {
      setEditingInvoice(inv);
    }
  };
  const [isTallyOpen, setIsTallyOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isGstOpen, setIsGstOpen] = useState(false);
  // Open invoice lives in the URL (/invoices/:invoiceId) so Back closes it and links can be shared
  const invoiceRoute = useRoutedSelection({ items: invoices, loading, paramName: "invoiceId", basePath: "/invoices" });
  const { activeCompany, activeCompanyId, setActiveCompany, ownsRecord, companyOfRecord } = useActiveCompany();
  // Only the header company's bills open; another company's bill shows a "switch company?" prompt instead
  const routedSelectedInvoice = invoiceRoute.selected;
  const otherCompanyBill = routedSelectedInvoice && !ownsRecord(routedSelectedInvoice) ? routedSelectedInvoice : null;
  const selectedInvoice = otherCompanyBill ? null : routedSelectedInvoice;
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState(null);

  // Working company (picked in the header): only its bills are listed
  const companyInvoices = useMemo(() => invoices.filter(ownsRecord), [invoices, ownsRecord]);
  const isAllCompanies = false;
  const isUnassigned = (r) => getRecordCompanyKey(r, companies) === UNASSIGNED_COMPANY;
  const listView = useListView("invoices", { onNew: () => setIsCreateOpen(true) });
  const columns = useMemo(
    () => invoiceColumns({ defaultCompany, showCompany: isAllCompanies && companies.length > 1 }),
    [defaultCompany, isAllCompanies, companies.length]
  );

  // Compute metrics for the selected company
  const totalBilled = useMemo(() => {
    return companyInvoices.reduce((sum, inv) => sum + Number(inv.grandTotal || 0), 0);
  }, [companyInvoices]);

  const totalOutstanding = useMemo(() => {
    return companyInvoices.reduce((sum, inv) => sum + Number(inv.balance || 0), 0);
  }, [companyInvoices]);

  // Filter and search
  const filteredInvoices = useMemo(() => {
    return companyInvoices.filter((inv) => {
      const term = searchTerm.toLowerCase().trim();
      const no = (inv.invoiceNo || "").toLowerCase();
      const customer = (inv.customerName || "").toLowerCase();

      const matchesSearch = !term || no.includes(term) || customer.includes(term);
      if (!matchesSearch) return false;

      const isNonGst = inv.isNonGst || String(inv.invoiceType || "").toLowerCase() === "normal";
      const status = String(inv.paymentStatus || "").toLowerCase();

      if (activeFilter === "GST") {
        return !isNonGst;
      }
      if (activeFilter === "NORMAL") {
        return isNonGst;
      }
      if (activeFilter === "UNPAID") {
        return status === "unpaid" || status === "partial";
      }
      if (activeFilter === "PAID") {
        return status === "paid";
      }

      return true;
    });
  }, [companyInvoices, searchTerm, activeFilter]);

  // Desktop: an open invoice shows beside the list (in the table's sort order) instead of in a dialog
  const showSplit = listView.isDesktop && !!selectedInvoice;
  const orderedInvoices = useMemo(
    () => (showSplit ? sortRows(filteredInvoices, columns, readStoredSort("invoices")) : filteredInvoices),
    [showSplit, filteredInvoices, columns]
  );
  const selectedIndex = showSplit ? orderedInvoices.findIndex((inv) => inv.id === selectedInvoice.id) : -1;
  useSplitScroll(showSplit);
  useSetPageHeading(showSplit ? `#${selectedInvoice.invoiceNo}` : null, invoiceRoute.close);

  // Render the filtered list in chunks of 30, appending as the user scrolls
  const invoiceList = useIncrementalList(filteredInvoices, {
    resetKey: `${activeCompanyId}|${activeFilter}|${searchTerm}`
  });

  const handleInvoiceCreated = async (payload) => {
    await addInvoice(payload);
  };

  const handleRecordPayment = async (invoiceId, paymentData) => {
    await recordPayment(invoiceId, paymentData);
  };

  return (
    <div className="invoices-page">
      {showSplit ? (
        <SplitPane
          storageKey="invoices"
          left={(
            <RecordList
              rows={orderedInvoices}
              selectedId={selectedInvoice.id}
              onSelect={(inv) => invoiceRoute.step(inv.id)}
              renderRow={(inv) => ({
                title: inv.invoiceNo,
                subtitle: `${inv.customerName} • ${formatAppDate(inv.invoiceDate, inv.companyDetails?.dateFormat || defaultCompany?.dateFormat)}`,
                amount: `₹${Number(inv.grandTotal || 0).toLocaleString("en-IN")}`,
                badge: <Badge variant={paymentStatusVariant(inv.paymentStatus)}>{inv.paymentStatus || "Unpaid"}</Badge>
              })}
              searchValue={searchTerm}
              onSearch={setSearchTerm}
              searchPlaceholder="Search by invoice # or customer name..."
              noun="bills"
              resetKey={`${activeCompanyId}|${activeFilter}|${searchTerm}`}
            />
          )}
          right={(
            <>
              <RecordNav
                index={selectedIndex}
                total={orderedInvoices.length}
                onPrev={() => invoiceRoute.step(orderedInvoices[selectedIndex - 1].id)}
                onNext={() => invoiceRoute.step(orderedInvoices[selectedIndex + 1].id)}
                onClose={invoiceRoute.close}
              />
              <InvoiceDetailModal
                key={selectedInvoice.id}
                variant="panel"
                isOpen
                onClose={invoiceRoute.close}
                invoice={selectedInvoice}
                onRecordPayment={handleRecordPayment}
                onDeleteInvoice={removeInvoice}
                onEditInvoice={openEditor}
                onPaymentsChanged={refetchInvoices}
              />
            </>
          )}
        />
      ) : (
        <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "0.5rem", width: "100%", flexWrap: "wrap" }}>
          <div style={{ minWidth: "140px", flex: 1 }}>
            <h2 style={{ fontSize: "1.25rem", fontWeight: "700" }}>Invoices & Bills</h2>
            <p style={{ fontSize: "0.8rem", color: "var(--text-dim)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {filteredInvoices.length} {filteredInvoices.length === 1 ? "bill" : "bills"} recorded
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
            <Button size="sm" variant="secondary" onClick={() => setIsGstOpen(true)} style={{ fontSize: "0.78rem" }}>
              🏛️ GST Report
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsHistoryOpen(true)} style={{ fontSize: "0.78rem" }}>
              📜 Payment History
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setIsTallyOpen(true)} style={{ fontSize: "0.78rem" }}>
              💰 Collect Payment
            </Button>
            <Button size="sm" variant="primary" onClick={() => setIsCreateOpen(true)} style={{ fontSize: "0.78rem" }}>
              New Invoice
            </Button>
          </div>
        </div>

        <OtherCompanyPrompt
          isOpen={!!otherCompanyBill}
          kind="invoice"
          billNo={otherCompanyBill?.invoiceNo || ""}
          company={otherCompanyBill ? companyOfRecord(otherCompanyBill) : null}
          onSwitch={() => setActiveCompany(companyOfRecord(otherCompanyBill).id)}
          onCancel={invoiceRoute.close}
        />
        {invoiceRoute.notFound && (
          <div style={{ background: "rgba(245, 158, 11, 0.12)", border: "1px solid rgba(245, 158, 11, 0.35)", color: "var(--warning)", padding: "0.55rem 0.85rem", borderRadius: "var(--radius-md)", fontSize: "0.82rem", marginBottom: "0.75rem" }}>
            ⚠️ That invoice could not be found. It may have been deleted.
          </div>
        )}

        {/* KPI Metrics Row */}
        <div className="metrics-row">
          <div className="metric-card">
            <div className="metric-label">Total Billed{activeCompany ? ` · ${activeCompany.name}` : ""}</div>
            <div className="metric-value">₹{totalBilled.toLocaleString("en-IN")}</div>
          </div>
          <div className="metric-card" style={{ borderLeft: totalOutstanding > 0 ? "3px solid var(--warning)" : "1px solid var(--border-subtle)" }}>
            <div className="metric-label">Outstanding{activeCompany ? ` · ${activeCompany.name}` : ""}</div>
            <div className="metric-value" style={{ color: totalOutstanding > 0 ? "var(--warning)" : "var(--success)" }}>
              ₹{totalOutstanding.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* Filter pills + search (one sticky toolbar row on desktop) */}
        <div className="list-toolbar">
          <div className="filter-pills-row">
            <button
              type="button"
              className={`filter-pill ${activeFilter === "ALL" ? "active" : ""}`}
              onClick={() => setActiveFilter("ALL")}
            >
              All ({companyInvoices.length})
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "GST" ? "active" : ""}`}
              onClick={() => setActiveFilter("GST")}
            >
              GST Bills
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "NORMAL" ? "active" : ""}`}
              onClick={() => setActiveFilter("NORMAL")}
            >
              Non-GST Bills
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "UNPAID" ? "active" : ""}`}
              onClick={() => setActiveFilter("UNPAID")}
            >
              Unpaid
            </button>
            <button
              type="button"
              className={`filter-pill ${activeFilter === "PAID" ? "active" : ""}`}
              onClick={() => setActiveFilter("PAID")}
            >
              Paid
            </button>
          </div>

          {/* Search Input */}
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search by invoice # or customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <ViewToggle view={listView.view} onChange={listView.setView} />
        </div>

        {loading && (
          <div className="state-box">
            <div className="state-icon">⏳</div>
            <div className="state-title">Loading Invoices...</div>
          </div>
        )}

        {error && (
          <div className="state-box">
            <div className="state-icon">⚠️</div>
            <div className="state-title">Error Loading Invoices</div>
            <div className="state-desc">{error}</div>
          </div>
        )}

        {!loading && !error && filteredInvoices.length === 0 && (
          <div className="state-box">
            <div className="state-icon">🧾</div>
            <div className="state-title">No invoices found</div>
            <div className="state-desc">
              {searchTerm ? "No invoice matches your search criteria." : "Create your first sales invoice with automatic stock deduction."}
            </div>
            <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
              Create First Invoice
            </Button>
          </div>
        )}

        {listView.showTable ? (
          <DataTable
            columns={columns}
            rows={filteredInvoices}
            onRowClick={(inv) => invoiceRoute.open(inv.id)}
            selectedId={selectedInvoice?.id}
            storageKey="invoices"
            noun="bills"
            resetKey={`${activeCompanyId}|${activeFilter}|${searchTerm}`}
          />
        ) : (
          <>
            {/* Invoices List */}
            <div className="invoices-list card-grid">
              {invoiceList.visibleItems.map((inv) => {
                const isPaid = inv.paymentStatus === "Paid";
                const statusVariant = isPaid ? "success" : (inv.paymentStatus === "Partial" ? "warning" : "danger");

                return (
                  <Card
                    key={inv.id}
                    onClick={() => invoiceRoute.open(inv.id)}
                    style={{ cursor: "pointer", marginBottom: "0.75rem" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ fontWeight: "700", color: "var(--text-main)", fontSize: "1rem" }}>
                            {inv.invoiceNo}
                          </span>
                          {inv.invoiceType === "Normal" || inv.isNonGst ? (
                            <span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: "4px", background: "var(--bg-tag, rgba(255,255,255,0.08))", color: "var(--text-muted)", fontWeight: "600", border: "1px solid var(--border-subtle)" }}>
                              Non-GST
                            </span>
                          ) : (
                            <span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: "4px", background: "rgba(56, 189, 248, 0.15)", color: "#38bdf8", fontWeight: "600", border: "1px solid rgba(56, 189, 248, 0.3)" }}>
                              GST
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "2px" }}>
                          {inv.customerName} • {(inv.lineItems || []).length} item{(inv.lineItems || []).length === 1 ? "" : "s"}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "4px" }}>
                          📅 {formatAppDate(inv.invoiceDate, inv.companyDetails?.dateFormat || defaultCompany?.dateFormat)}
                        </div>
                        {isUnassigned(inv) && (
                          <div style={{ fontSize: "0.68rem", color: "var(--warning, #b45309)", marginTop: "2px", fontWeight: 600 }}>No company</div>
                        )}
                        {isAllCompanies && companies.length > 1 && (inv.companyName || inv.companyDetails?.name) && (
                          <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
                            🏢 {inv.companyName || inv.companyDetails?.name}
                          </div>
                        )}
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#38bdf8", marginBottom: "4px" }}>
                          ₹{Number(inv.grandTotal || 0).toLocaleString("en-IN")}
                        </div>
                        <Badge variant={statusVariant}>
                          {inv.paymentStatus || "Unpaid"}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <ListSentinel
              sentinelRef={invoiceList.sentinelRef}
              hasMore={invoiceList.hasMore}
              visibleCount={invoiceList.visibleCount}
              total={invoiceList.total}
              onShowMore={invoiceList.showMore}
            />
          </>
        )}

        <StickyFooter
          shown={filteredInvoices.length}
          total={companyInvoices.length}
          noun="bills"
          chips={[
            { label: "Billed", value: `₹${totalBilled.toLocaleString("en-IN")}` },
            { label: "Outstanding", value: `₹${totalOutstanding.toLocaleString("en-IN")}`, tone: totalOutstanding > 0 ? "warning" : "success" }
          ]}
        />
        </>
      )}

      {/* Floating Action Button */}
      <button
        type="button"
        className="fab"
        onClick={() => setIsCreateOpen(true)}
        aria-label="Create Invoice"
      >
        +
      </button>

      {/* Stepped Invoice Creation Flow */}
      <InvoiceCreateFlow
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onInvoiceCreated={handleInvoiceCreated}
        initialCompanyId={activeCompanyId}
      />

      {/* Edit a saved invoice: the same steps, filled in */}
      <InvoiceCreateFlow
        isOpen={!!editingInvoice}
        onClose={() => setEditingInvoice(null)}
        editingInvoice={editingInvoice}
        onInvoiceUpdated={refetchInvoices}
      />

      {/* Invoice Detail / WhatsApp / Payment Modal */}
      <InvoiceDetailModal
        isOpen={!showSplit && !!selectedInvoice}
        onClose={invoiceRoute.close}
        invoice={selectedInvoice}
        onRecordPayment={handleRecordPayment}
        onDeleteInvoice={removeInvoice}
                onEditInvoice={openEditor}
        onPaymentsChanged={refetchInvoices}
      />

      {/* Multi-Invoice Customer Payment Tally Modal */}
      <PaymentTallyModal
        isOpen={isTallyOpen}
        onClose={() => setIsTallyOpen(false)}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={isHistoryOpen}
        onPaymentsChanged={refetchInvoices}
        onClose={() => setIsHistoryOpen(false)}
        filterType="PAYMENT_IN"
        onViewReceipt={(payment) => {
          setIsHistoryOpen(false);
          setSelectedReceiptPayment(payment);
        }}
      />

      {/* Payment Receipt Viewer */}
      <PaymentReceiptModal
        isOpen={!!selectedReceiptPayment}
        onClose={() => setSelectedReceiptPayment(null)}
        payment={selectedReceiptPayment}
      />

      {/* GST Filing Report & Export Modal */}
      <GstReportModal
        isOpen={isGstOpen}
        onClose={() => setIsGstOpen(false)}
      />
    </div>
  );
}

export default InvoicesPage;
